import React, { useEffect, Suspense, lazy } from 'react';
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate
} from 'react-router-dom';
// 移除未使用的 UNSAFE API 导入以避免控制台警告
import { ThemeProvider, createTheme, CssBaseline } from '@mui/material';
import Layout from './components/layout/Layout';
import ErrorBoundary from './components/common/ErrorBoundary';
import config from './config';
import './assets/styles/global.css';
// 导入 Google Analytics
import { initGA, testGA } from './utils/analytics';
// 导入全局错误处理器
import './utils/errorHandler';
import { Analytics } from "@vercel/analytics/react";

// Lazy-loaded components
const OrderTrackPage = lazy(() => import('./pages/OrderTrackPage'));

// LoadingFallback component
const LoadingFallback = () => (
  <div style={{
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    height: 'calc(100vh - 100px)',
    width: '100%'
  }}>
    <div style={{
      textAlign: 'center',
      fontFamily: 'Jost, Inter, sans-serif'
    }}>
      <div className="loader" style={{
        border: '4px solid #f3f3f3',
        borderTop: '4px solid #121212',
        borderRadius: '50%',
        width: '40px',
        height: '40px',
        margin: '0 auto 20px',
        animation: 'spin 1s linear infinite'
      }}></div>
      <p>Loading content...</p>
      <style>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  </div>
);

// Updated black and white theme with sans-serif fonts
const theme = createTheme({
  palette: {
    mode: 'light',
    primary: {
      main: '#121212',
      light: '#333333',
      dark: '#000000',
    },
    secondary: {
      main: '#555555',
      light: '#777777',
      dark: '#333333',
    },
    background: {
      default: '#ffffff',
      paper: '#f9f9f9',
    },
    text: {
      primary: '#121212',
      secondary: '#555555',
    },
    divider: '#e0e0e0',
  },
  typography: {
    fontFamily: [
      'Jost',
      'Inter',
      '-apple-system',
      'BlinkMacSystemFont',
      '"Segoe UI"',
      'Roboto',
      'Arial',
      'sans-serif',
    ].join(','),
    h4: {
      fontWeight: 700,
      letterSpacing: '-0.01em',
    },
    h6: {
      fontWeight: 600,
      letterSpacing: '-0.005em',
    },
    body1: {
      fontSize: '1rem',
      lineHeight: 1.5,
    },
    body2: {
      fontSize: '0.875rem',
      lineHeight: 1.5,
    },
    button: {
      textTransform: 'none',
      fontWeight: 600,
    },
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 6,
          padding: '8px 20px',
          fontWeight: 600,
          boxShadow: 'none',
          '&:hover': {
            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
          },
        },
        contained: {
          backgroundColor: '#121212',
          color: '#ffffff',
          '&:hover': {
            backgroundColor: '#333333',
          },
        },
        outlined: {
          borderColor: '#e0e0e0',
          '&:hover': {
            borderColor: '#121212',
            backgroundColor: 'rgba(18, 18, 18, 0.04)',
          },
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          boxShadow: '0 1px 4px rgba(0, 0, 0, 0.08)',
          border: '1px solid #f0f0f0',
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          fontWeight: 600,
          borderRadius: 4,
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            borderRadius: 6,
          },
        },
      },
    },
    MuiDivider: {
      styleOverrides: {
        root: {
          backgroundColor: '#e0e0e0',
        },
      },
    },
    MuiTab: {
      styleOverrides: {
        root: {
          fontWeight: 600,
          letterSpacing: '0.01em',
        }
      }
    }
  },
  shape: {
    borderRadius: 6,
  },
});

// 移除可能导致警告的 future flags，使用当前稳定版本
// const v7_startTransition = true;
// const v7_relativeSplatPath = true;

function App() {
  // 使用来自配置文件的基础路径，确保格式正确（末尾有斜杠）
  let basename = config.BASE_PATH;

  // 确保 basename 格式正确
  if (!basename.endsWith('/')) {
    basename += '/';
  }

  // 使用更受控的日志记录方法，避免过多控制台输出
  if (process.env.NODE_ENV === 'development') {
    console.info(`[App] Base path configured: '${basename}'`);
  }

  // 初始化 Google Analytics
  useEffect(() => {
    // 初始化 GA
    initGA();

    // 延迟测试 GA 是否正常工作
    setTimeout(() => {
      testGA();
    }, 2000);
  }, []);

  // 检查当前路径，如果不匹配则可能需要提示用户
  useEffect(() => {
    const currentPath = window.location.pathname;
    const expectedBasePath = basename.endsWith('/')
      ? basename.slice(0, -1)
      : basename;

    if (currentPath !== expectedBasePath &&
      currentPath !== basename &&
      !currentPath.startsWith(basename)) {
      console.warn(
        `[App] Path mismatch: Current (${currentPath}) vs Expected (${basename})`
      );
      // 我们可以选择自动重定向，但这里只记录错误
    }
  }, [basename]);

  return (
    <ErrorBoundary name="App">
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <Router basename={basename}>
          <Layout>
            <ErrorBoundary name="Routes">
              <Suspense fallback={<LoadingFallback />}>
                <Routes>
                  <Route path="/" element={<OrderTrackPage />} />
                  <Route path="/track" element={<OrderTrackPage />} />
                  <Route path="*" element={<Navigate to="/" replace />} />
                </Routes>
              </Suspense>
            </ErrorBoundary>
          </Layout>
        </Router>
        <Analytics />
      </ThemeProvider>
    </ErrorBoundary>
  );
}

export default App;
